import { z } from "zod";

const permissionName = z.string().regex(/^[a-z][a-z0-9_.:-]{0,63}$/);
export const apiRegistrationSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,62}$/),
  version: z.literal("v1"),
  documentationUrl: z.url().refine((value) => new URL(value).protocol === "https:"),
  permissions: z.array(z.object({
    id: permissionName,
    description: z.string().min(1).max(240),
    access: z.enum(["user", "owner", "admin"]),
  })).min(1).max(50),
  routes: z.array(z.object({
    method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"]),
    path: z.string().max(300).regex(/^\/(?:[a-zA-Z0-9_-]+|:[a-zA-Z][a-zA-Z0-9_]*)(?:\/(?:[a-zA-Z0-9_-]+|:[a-zA-Z][a-zA-Z0-9_]*))*$/),
    permissions: z.array(permissionName).min(1).max(50),
  })).min(1).max(100),
}).superRefine((api, ctx) => {
  const ids = new Set(api.permissions.map((p) => p.id));
  if (ids.size !== api.permissions.length) ctx.addIssue({ code: "custom", message: "API permissions must be unique" });
  const routes = new Set<string>();
  for (const [index, route] of api.routes.entries()) {
    const key = `${route.method} ${route.path.replace(/:[^/]+/g, ":param")}`;
    if (routes.has(key)) ctx.addIssue({ code: "custom", message: "API routes must be unique" });
    routes.add(key);
    if (route.permissions.some((p) => !ids.has(p))) ctx.addIssue({ code: "custom", message: "Route references an undeclared permission" });
    const segments = route.path.split("/");
    if (api.routes.slice(0, index).some((other) => other.method === route.method && other.path.split("/").length === segments.length &&
      other.path.split("/").every((s, i) => s.startsWith(":") || segments[i].startsWith(":") || s === segments[i]))) {
      ctx.addIssue({ code: "custom", message: "API routes must not overlap" });
    }
  }
});
export type ApiRegistration = z.infer<typeof apiRegistrationSchema>;

/** Exact segment matching; encoded separators, traversal and ambiguous paths fail closed. */
export function apiRoute(api: ApiRegistration, method: string, path: string) {
  if (!/^\/(?:[a-zA-Z0-9_-]+)(?:\/[a-zA-Z0-9_-]+)*$/.test(path)) return undefined;
  return api.routes.find((route) => route.method === method && route.path.split("/").length === path.split("/").length &&
    route.path.split("/").every((segment, i) => segment.startsWith(":") || segment === path.split("/")[i]));
}

export { apiSha256, boundedApiBody, type ApiPrincipal } from "./api-runtime";
import type { ApiPrincipal } from "./api-runtime";

export type ApiAuthorization = { ok: true; principal: ApiPrincipal; upstream: string } |
  { ok: false; status: number; code: string; retryAfter?: number };

export function apiError(status: number, code: string, requestId: string, retryAfter?: number) {
  const headers = new Headers({ "cache-control": "no-store", "x-content-type-options": "nosniff", "x-request-id": requestId });
  if (status === 401) headers.set("www-authenticate", 'Bearer realm="CheesyApps API"');
  if (retryAfter !== undefined) headers.set("retry-after", String(retryAfter));
  return Response.json({ error: { code, requestId } }, { status, headers });
}
